import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { type Surface, type Theme, readTheme, writeTheme } from '../lib/theme';

/**
 * Surface theme provider (design-language §9). Wrap each top-level surface:
 *   <ThemeRoot surface="live">…</ThemeRoot>   <ThemeRoot surface="admin">…</ThemeRoot>
 *
 * Sets [data-theme] on both the wrapper (local scoping) and <html> (so the page canvas/body
 * matches), persists per-surface flips, and exposes useTheme() for the toggle.
 */

type ThemeCtx = {
  surface: Surface;
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
};
const Ctx = createContext<ThemeCtx | null>(null);

export function ThemeRoot({
  surface,
  children,
  className,
}: {
  surface: Surface;
  children: ReactNode;
  className?: string;
}) {
  const [theme, setThemeState] = useState<Theme>(() => readTheme(surface));

  // Drive the document so body bg / color-scheme follow the active surface.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const setTheme = useCallback(
    (t: Theme) => {
      setThemeState(t);
      writeTheme(surface, t);
    },
    [surface]
  );
  const toggle = useCallback(
    () => setTheme(theme === 'night' ? 'day' : 'night'),
    [theme, setTheme]
  );

  return (
    <Ctx.Provider value={{ surface, theme, setTheme, toggle }}>
      <div data-theme={theme} className={className}>
        {children}
      </div>
    </Ctx.Provider>
  );
}

export function useTheme(): ThemeCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTheme must be used within <ThemeRoot>');
  return v;
}
